import React, { useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { useModalDismiss } from '../ui/useModalDismiss';
import MaterialIcon from '../ui/MaterialIcon';
import { ROSTER_COLUMNS, planRosterImport } from '../../lib/rosterImport';
import { rosterImportService } from '../../services/rosterImportService';

const RELATIONSHIP_LABELS = {
  owner: 'Owner', co_owner: 'Co-owner', spouse: 'Spouse', tenant: 'Tenant',
  family_member: 'Family member', property_manager: 'Property manager', board_member: 'Board member',
};

const ghostButton = 'border border-secondary text-secondary px-6 py-3 rounded-full font-bold text-sm hover:bg-secondary hover:text-white transition-all disabled:opacity-50';
const primaryButton = 'bg-secondary text-white px-6 py-3 rounded-full font-bold text-sm hover:brightness-110 active:scale-95 transition-all disabled:opacity-50';

// Bulk-add homes and people from the roster template (CSV or Excel). Step 1: template
// and file. Step 2: preview (matched homes, new homes, rows left out). Step 3: one
// all-or-nothing import; people arrive as "Not invited" for the usual Send invites.
const RosterImportModal = ({ open, hoa, onClose, onImported }) => {
  const [file, setFile] = useState(null);
  const [reading, setReading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState('');
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const busy = reading || importing;
  useModalDismiss({ isOpen: open, onClose: busy ? () => {} : onClose });

  const plan = useMemo(() => {
    if (!preview) return null;
    return planRosterImport(preview.roster.people, preview.homes, {
      city: hoa?.city, state: hoa?.state, zip: hoa?.zip,
    });
  }, [preview, hoa]);

  if (!open) return null;

  const handleFile = async (chosen) => {
    setFile(chosen);
    setPreview(null);
    setResult(null);
    setError('');
    if (!chosen) return;
    setReading(true);
    try {
      const [roster, homes] = await Promise.all([
        rosterImportService.readFile(chosen),
        rosterImportService.listHomes(hoa?.id),
      ]);
      setPreview({ roster, homes });
    } catch (e) {
      setError(e?.message || 'That file could not be read.');
    } finally {
      setReading(false);
    }
  };

  const handleImport = async () => {
    if (!plan?.rows.length) return;
    setImporting(true);
    setError('');
    try {
      const outcome = await rosterImportService.importRows(hoa.id, plan.rows);
      setResult(outcome);
      onImported?.(outcome);
    } catch (e) {
      setError(e?.message || 'Import failed; nothing was added.');
    } finally {
      setImporting(false);
    }
  };

  const roster = preview?.roster;
  const people = roster?.people || [];
  const homeIds = new Map((plan?.rows || []).map((r) => [r.line, r.property_id]));

  return (
    <motion.div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={busy ? undefined : onClose}
      role="presentation"
      data-modal-backdrop="true"
    >
      <motion.div
        className="bg-white rounded-2xl warm-tactile-shadow border border-amber-100/30 w-full max-w-3xl max-h-[90vh] overflow-y-auto p-6 sm:p-8 space-y-6"
        initial={{ scale: 0.97, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.97, opacity: 0 }}
        onClick={(event) => event.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="roster-import-title"
        data-testid="roster-import-modal"
      >
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-secondary/10 rounded-xl flex items-center justify-center">
              <MaterialIcon name="upload_file" className="text-secondary" size={24} />
            </div>
            <div>
              <h3 id="roster-import-title" className="text-xl font-bold text-primary-dark">Import from a spreadsheet</h3>
              <p className="text-sm font-medium text-slate-500 mt-1">
                Add homes and people in bulk. Nobody is emailed until you send invitations.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="w-10 h-10 rounded-full hover:bg-section-bg flex items-center justify-center disabled:opacity-50"
            aria-label="Close import"
          >
            <MaterialIcon name="close" size={20} />
          </button>
        </div>

        {!result && (
          <section className="bg-section-bg rounded-2xl p-5 space-y-4">
            <p className="text-xs font-bold uppercase tracking-widest text-slate-500">1 · Fill in the template</p>
            <p className="text-sm font-medium text-slate-600">
              One row per person; two people in the same home are two rows with the same address and unit.
              The three rows marked EXAMPLE are skipped. Save as .csv or .xlsx.
            </p>
            <details className="text-sm font-medium text-slate-600">
              <summary className="cursor-pointer font-bold text-primary-dark">Columns</summary>
              <ul className="mt-2 space-y-1">
                {ROSTER_COLUMNS.map((c) => (
                  <li key={c.key}>
                    <span className="font-bold text-primary-dark">{c.header}</span>
                    {c.required ? ' (required)' : ''}{c.note ? `: ${c.note}` : ''}
                  </li>
                ))}
              </ul>
            </details>
            <div className="flex flex-wrap gap-3">
              <button type="button" className={ghostButton} onClick={() => rosterImportService.downloadTemplate()}>
                Download template
              </button>
              <label className={`${primaryButton} cursor-pointer inline-flex items-center gap-2`}>
                <MaterialIcon name="upload" size={18} />
                {file ? 'Choose another file' : 'Choose file'}
                <input
                  type="file"
                  accept=".csv,.xlsx,.xls,text/csv"
                  className="sr-only"
                  disabled={busy}
                  onChange={(e) => handleFile(e.target.files?.[0] || null)}
                  data-testid="roster-file-input"
                />
              </label>
            </div>
            {file && <p className="text-xs font-bold uppercase tracking-tighter text-slate-500">{file.name}</p>}
          </section>
        )}

        {reading && <p className="text-sm font-medium text-slate-500">Reading the file…</p>}
        {error && <p className="text-sm font-medium text-red-600" role="alert">{error}</p>}

        {roster && !result && roster.missingHeaders.length > 0 && (
          <p className="text-sm font-medium text-red-600" role="alert">
            This file is missing: {roster.missingHeaders.join(', ')}. Start from the template, or rename your columns to match it.
          </p>
        )}

        {plan && !result && roster.missingHeaders.length === 0 && (
          <section className="space-y-4" data-testid="roster-preview">
            <p className="text-xs font-bold uppercase tracking-widest text-slate-500">2 · Check before importing</p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {[
                { label: 'People', value: people.length },
                { label: 'Homes already listed', value: plan.matchedHomes },
                { label: 'New homes', value: plan.newHomes },
                { label: 'Rows left out', value: roster.issues.length },
              ].map((s) => (
                <div key={s.label} className="bg-section-bg rounded-2xl p-3">
                  <div className="text-2xl font-extrabold tracking-tight text-secondary">{s.value}</div>
                  <div className="text-[10px] font-bold uppercase tracking-tighter text-slate-500">{s.label}</div>
                </div>
              ))}
            </div>
            {roster.unknownHeaders.length > 0 && (
              <p className="text-sm font-medium text-slate-500">Columns not used: {roster.unknownHeaders.join(', ')}.</p>
            )}
            {roster.issues.length > 0 && (
              <ul className="text-sm font-medium text-amber-800 bg-amber-50 rounded-2xl p-4 space-y-1">
                {roster.issues.slice(0, 20).map((i) => <li key={`${i.line}-${i.message}`}>Row {i.line}: {i.message}</li>)}
                {roster.issues.length > 20 && <li>…and {roster.issues.length - 20} more.</li>}
              </ul>
            )}
            {people.length > 0 && (
              <div className="overflow-x-auto max-h-72 overflow-y-auto rounded-2xl border border-amber-100/40">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 bg-section-bg">
                    <tr>
                      {['Row', 'Home', 'Name', 'Email', 'Relationship', ''].map((h) => (
                        <th key={h} className="text-left px-3 py-2 text-[10px] font-bold uppercase tracking-tighter text-slate-500">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {people.slice(0, 200).map((p) => (
                      <tr key={p.line}>
                        <td className="px-3 py-2 text-slate-500">{p.line}</td>
                        <td className="px-3 py-2 font-medium text-primary-dark whitespace-nowrap">{p.street}{p.unit ? ` #${p.unit}` : ''}</td>
                        <td className="px-3 py-2 text-slate-700">{p.member_name || '—'}</td>
                        <td className="px-3 py-2 text-slate-600">{p.email || 'No email'}</td>
                        <td className="px-3 py-2 text-slate-600">{RELATIONSHIP_LABELS[p.link_type]}</td>
                        <td className="px-3 py-2 text-[10px] font-bold uppercase tracking-tighter text-slate-500 whitespace-nowrap">
                          {homeIds.get(p.line) ? 'Listed home' : 'New home'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <div className="flex flex-wrap gap-3">
              <button type="button" className={primaryButton} disabled={busy || people.length === 0} onClick={handleImport}>
                {importing ? 'Importing…' : `Add ${people.length} ${people.length === 1 ? 'person' : 'people'}`}
              </button>
              <button type="button" className={ghostButton} disabled={busy} onClick={onClose}>Cancel</button>
            </div>
          </section>
        )}

        {result && (
          <section className="space-y-4" data-testid="roster-result">
            <p className="text-xs font-bold uppercase tracking-widest text-slate-500">3 · Done</p>
            <p className="text-base font-medium text-primary-dark">
              Added {result.people_added} {result.people_added === 1 ? 'person' : 'people'}
              {result.homes_created ? ` and ${result.homes_created} new ${result.homes_created === 1 ? 'home' : 'homes'}` : ''}.
              They show as Not invited (or No email); select them and use Send invites when you are ready.
            </p>
            {result.people_skipped?.length > 0 && (
              <ul className="text-sm font-medium text-slate-600 bg-section-bg rounded-2xl p-4 space-y-1">
                {result.people_skipped.slice(0, 20).map((s) => <li key={`${s.line}-${s.reason}`}>Row {s.line}: {s.reason}</li>)}
                {result.people_skipped.length > 20 && <li>…and {result.people_skipped.length - 20} more.</li>}
              </ul>
            )}
            <button type="button" className={primaryButton} onClick={onClose}>Close</button>
          </section>
        )}
      </motion.div>
    </motion.div>
  );
};

export default RosterImportModal;
